import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "../../DashboardPage/lib/utils";
import { buttonVariants, type ButtonVariantProps } from "./buttonVariants";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonVariantProps {
  /**
   * Render the single child (e.g. a react-router `<Link>`) with button styling
   * instead of a `<button>`. `loading` is ignored in this mode.
   */
  asChild?: boolean;
  /** Shows a spinner, disables the button and sets aria-busy. */
  loading?: boolean;
}

/**
 * The app's button. Variants and sizes are defined in buttonVariants.ts.
 *
 *   <Button onClick={save}>Save</Button>
 *   <Button variant="danger" loading={deleting}>Delete</Button>
 *   <Button asChild variant="secondary"><Link to="/roster">Roster</Link></Button>
 *
 * Like a native <button>, `type` defaults to "submit" inside a form — pass
 * type="button" for anything that isn't the form's submit.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
    ref
  ) => {
    const classes = cn(buttonVariants({ variant, size }), className);

    if (asChild) {
      return (
        <Slot ref={ref} className={classes} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && (
          <svg
            className="w-4 h-4 animate-spin shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            data-testid="button-spinner"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
export { Button, buttonVariants };
